function collectFromNode(node, parts, depth = 0) {
  if (!node || depth > 5) return;
  if (typeof node === 'string') {
    parts.push(node);
    return;
  }
  if (typeof node !== 'object') return;

  for (const field of ['message', 'shortMessage', 'details', 'reason']) {
    if (node[field]) parts.push(String(node[field]));
  }
  if (node.error?.message) parts.push(String(node.error.message));
  if (node.data?.message) parts.push(String(node.data.message));
  if (typeof node.code === 'string') parts.push(node.code);

  collectFromNode(node.cause, parts, depth + 1);
}

function collectErrorText(err) {
  const parts = [];
  collectFromNode(err, parts);
  return parts
    .filter(Boolean)
    .map((p) => String(p).toLowerCase())
    .join(' ');
}

const GAS_ERROR_PATTERNS = [
  'insufficient funds for transfer',
  'insufficient funds for intrinsic',
  'exceeds the balance of the account',
  'total cost (gas * gas fee + value)',
  'intrinsic gas too low',
  'insufficient funds',
  'insufficient balance',
  'not enough funds',
  'insufficient_funds',
  'gas required exceeds allowance',
  'transaction_broadcast_failure',
  '交易通道 gas',
  '交易通道正在准备',
  '交易处理中',
  'transaction processing',
  '交易通道代发失败',
];

const TRADING_CHANNEL_PREPARING_PATTERNS = [
  '交易处理中',
  '交易通道正在准备',
  '交易通道 gas',
  'trading channel is preparing',
  'transaction processing',
];

/** 托管通道 Gas 补款/到账中（可自动重试，非用户余额不足） */
export function isTradingChannelPreparingError(err) {
  const raw = collectErrorText(err);
  if (!raw) return false;
  return TRADING_CHANNEL_PREPARING_PATTERNS.some((pattern) => raw.includes(pattern));
}

/**
 * 链上授权/交易因 POL（gas）不足失败（viem / ethers 常见报错）
 * @param {unknown} err
 * @param {string} [_phase] 保留参数以兼容调用方
 */
export function isInsufficientGasError(err, _phase = 'idle') {
  void _phase;
  const raw = collectErrorText(err);
  if (!raw) return false;
  return GAS_ERROR_PATTERNS.some((pattern) => raw.includes(pattern));
}

/** viem/ethers 等技术长文，不宜直接展示给用户 */
export function isTechnicalChainError(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return false;
  if (raw.length > 180) return true;
  const lower = raw.toLowerCase();
  const markers = [
    'version: viem',
    'request arguments',
    'contract call',
    'maxfeepergas',
    'maxpriorityfeepergas',
    'shortmessage:',
    'transaction:',
    'docs: http',
  ];
  return markers.some((marker) => lower.includes(marker));
}

/** 浏览器/代理层网络失败（RPC Failed to fetch 等） */
export function isTradeNetworkFetchError(err) {
  const raw = collectErrorText(err);
  if (!raw) return false;
  const patterns = [
    'failed to fetch',
    'networkerror',
    'network request failed',
    'load failed',
    'fetch failed',
    'econnreset',
    'econnrefused',
    'etimedout',
    'network timeout',
    'proxy error',
  ];
  return patterns.some((p) => raw.includes(p));
}

/**
 * 将 gas 不足错误映射为友好 i18n 文案
 * @returns {string|null}
 */
export function resolveTradeGasErrorMessage(err, t, { privy = false } = {}) {
  if (!isInsufficientGasError(err)) return null;
  return privy ? t('tradeDialog.privyInsufficientGas') : t('tradeDialog.insufficientGas');
}

/**
 * 用户在钱包中取消 / 拒绝签名或交易（MetaMask / WalletConnect / ethers / viem）
 */
export function isUserRejectedError(err) {
  const code = err?.code ?? err?.info?.error?.code ?? err?.error?.code ?? err?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED' || code === 'REJECTED') return true;

  const raw = collectErrorText(err);
  if (!raw) return false;
  const patterns = [
    'user rejected',
    'user denied',
    'user cancelled',
    'user canceled',
    'rejected the request',
    'rejected the transaction',
    'denied transaction signature',
    'denied message signature',
    'request rejected',
    'transaction rejected',
    'ethers-user-denied',
    'action_rejected',
    'user closed modal',
    'modal closed by user',
  ];
  return patterns.some((p) => raw.includes(p));
}

/**
 * 用户取消钱包操作 → 友好文案；非取消返回 null
 * @param {'approval'|'sign'|'transfer'} [phase]
 */
export function resolveUserRejectMessage(err, t, phase = 'sign') {
  if (!isUserRejectedError(err) || typeof t !== 'function') return null;
  if (phase === 'approval') return t('tradeDialog.approvalRejected');
  if (phase === 'transfer') return t('withdrawDialog.transferRejected');
  return t('tradeDialog.signRejected');
}
